/**
 * Global arama istemcisi (WP 2.11): gerçek `POST /search/query` ucu, önbellek anahtarı ve hata sınıflandırması. Sahte sonuç
 * yok. Sorgu metni URL'ye yazılmaz (gövdede gider); önbellek anahtarı tenant + kullanıcı ile başlar (`qk.search`), yalnız
 * bellekte ve kısa ömürlüdür (kalıcı depolama / service worker yok, son aramalar saklanmaz).
 */
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  parseSearchQuery,
  type SearchQueryError,
  type SearchQueryInput,
  type SearchRecordType,
  type SearchResponse,
} from '@castinflow/shared';
import { ApiError, apiFetch } from '../../lib/api';
import { csrfHeaders } from '../../lib/session';
import { qk, useScope } from '../../lib/scope';

export function searchRecords(
  input: SearchQueryInput,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  return apiFetch<SearchResponse>('/search/query', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
}

/** Arama hatasının arayüz sınıfı (metin `searchStrings.error` içinde). */
export type SearchErrorKind = 'timeout' | 'rate' | 'invalid' | 'other';
export function searchErrorKind(e: unknown): SearchErrorKind {
  if (!(e instanceof ApiError)) return 'other';
  if (e.code === 'QUERY_TIMEOUT') return 'timeout';
  if (e.code === 'RATE_LIMITED') return 'rate';
  if (e.code === 'VALIDATION_FAILED') return 'invalid';
  return 'other';
}

/** İstemci tarafı ön denetim (sunucuyla AYNI ayrıştırıcı): geçersiz sorguda istek gönderilmez. */
export function validateQuery(q: string): { ok: true } | { ok: false; error: SearchQueryError } {
  const r = parseSearchQuery(q);
  return r.ok ? { ok: true } : { ok: false, error: r.error };
}

export interface SearchParams {
  q: string;
  types?: readonly SearchRecordType[];
  tagIds?: readonly string[];
  limit?: number;
}

function inputOf(p: SearchParams, cursor?: string): SearchQueryInput {
  return {
    q: p.q,
    ...(p.types && p.types.length > 0 ? { types: [...p.types] } : {}),
    ...(p.tagIds && p.tagIds.length > 0 ? { tagIds: [...p.tagIds] } : {}),
    ...(p.limit === undefined ? {} : { limit: p.limit }),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

const SEARCH_GC_MS = 30_000;

/** Tek sayfa (hızlı arama / "Hepsi" görünümü). Anahtar değişince önceki istek iptal edilir; eski yanıt yeniyi ezemez. */
export function useSearch(p: SearchParams | null) {
  const scope = useScope();
  const enabled = scope !== null && p !== null && validateQuery(p.q).ok;
  return useQuery({
    queryKey:
      scope !== null && p !== null
        ? qk.search(scope, { ...inputOf(p), cursor: null })
        : ['search-disabled'],
    queryFn: ({ signal }) => searchRecords(inputOf(p ?? { q: '' }), signal),
    enabled,
    retry: false,
    staleTime: 10_000,
    gcTime: SEARCH_GC_MS,
  });
}

/** Tek tip, cursor sayfalı sonuçlar (tam sonuç sayfası). */
export function useSearchPages(p: (SearchParams & { types: [SearchRecordType] }) | null) {
  const scope = useScope();
  const enabled = scope !== null && p !== null && validateQuery(p.q).ok;
  return useInfiniteQuery({
    queryKey:
      scope !== null && p !== null
        ? qk.search(scope, { ...inputOf(p), cursor: 'pages' })
        : ['search-disabled-pages'],
    queryFn: ({ signal, pageParam }) =>
      searchRecords(inputOf(p ?? { q: '' }, pageParam ?? undefined), signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.sections[0]?.nextCursor ?? null,
    enabled,
    retry: false,
    staleTime: 10_000,
    gcTime: SEARCH_GC_MS,
  });
}
